import { useState, FC } from 'react'
import { Link } from 'react-router-dom'
import Actions from '../../../actions'
import _ from 'lodash'
import { gaEvent } from '../../../common/services/analytics'
import { ProfileLink, Spinner } from '../../../components/generic'
import { getNominative } from '../../../common/utils'
import { ButtonOLD, Button } from '@lmc/ui-kit'
import { useSelector, useDispatch } from 'react-redux'
import { Home, ResidentItem, ResidentStatus, UserRole } from '@lmc/ts-common'
import { PlusOutlined } from '@ant-design/icons'
import { EmptyAvatars } from './CardComponents'
import { CardTitle } from './CardComponents/CardTitle'
import { useRoleAccess } from 'components/AccessControl'

const MAX_RESIDENTS_DISPLAYED_INIT = 10

const VIEW_BUTTON_TEXT = 'View All'

const getTitle = (preferredPeopleNominative: string) =>
  getNominative(preferredPeopleNominative, 'Residents')

const getAddResidentButtonText = (preferredPeopleNominative: string) => {
  if (
    preferredPeopleNominative !== 'Supported Person' &&
    preferredPeopleNominative !== 'Service Users (SU)'
  ) {
    return `Add a ${getNominative(preferredPeopleNominative, 'Resident')}`
  }
  return 'Add'
}

type Props = { residents?: ResidentItem[] }

export const ResidentsCard: FC<Props> = ({ residents }) => {
  const [maxResidentsDisplayed, setMaxResidentsDisplayed] = useState(
    MAX_RESIDENTS_DISPLAYED_INIT
  )

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin, UserRole.seniorCarer],
    [
      {
        i: 'create',
        this: 'Resident',
      },
    ]
  )

  const dispatch = useDispatch()

  const openModal = () => {
    const id = 'addResident'
    dispatch(Actions.openModal({ modalId: id }))
    dispatch(Actions.addForm({ formId: id }))
  }

  const triggerGAEvent = ({
    action,
    label,
  }: {
    action: string
    label?: string
  }) =>
    gaEvent({
      category: 'home-dashboard',
      action,
      label,
    })

  const showMore = () => {
    setMaxResidentsDisplayed(maxResidentsDisplayed + 20)
    triggerGAEvent({ action: 'Click show_more_residents' })
  }

  const renderResidents = (activeResidents: ResidentItem[]) => {
    const hiddenResidents = activeResidents.length > maxResidentsDisplayed

    return (
      <div>
        <div className="lmc-flex-grid" style={{ marginTop: 5 }}>
          {_.take(
            activeResidents,
            hiddenResidents ? maxResidentsDisplayed - 1 : maxResidentsDisplayed
          ).map((row) => (
            <ProfileLink
              onClick={() =>
                triggerGAEvent({ action: 'Click resident_profile_link' })
              }
              list="residents"
              key={row.id}
              name={row.name}
              id={row.id}
              picture={row.picture}
            />
          ))}
          {hiddenResidents ? (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a className="lmc-more-link" onClick={showMore}>
              More...
            </a>
          ) : null}
        </div>
      </div>
    )
  }

  const isLoading = residents?.length === 0 || undefined

  const renderNoResidents = () => <EmptyAvatars />

  const renderBody = () => {
    if (isLoading) return <Spinner style={{ paddingTop: 50 }} />

    const activeResidents = _.chain(residents)
      .filter({ status: ResidentStatus.active })
      .sortBy('name')
      .value()

    if (activeResidents && activeResidents.length)
      return renderResidents(activeResidents)

    return renderNoResidents()
  }

  const renderFooter = () => {
    const link = `/residents`
    if (isLoading) return null

    const hasResidents = !!residents?.length

    return (
      <div style={styles.footer}>
        {hasResidents && (
          <div style={styles.viewAllButton}>
            <Link
              onClick={() =>
                triggerGAEvent({ action: 'Click view all residents' })
              }
              to={link}
            >
              <ButtonOLD type="secondary">{VIEW_BUTTON_TEXT}</ButtonOLD>
            </Link>
          </div>
        )}
        <div id="intro-js-step-add-residents">
          <Button
            icon={<PlusOutlined />}
            variant="primary"
            onClick={() => {
              triggerGAEvent({ action: 'Click create_resident' })
              openModal()
            }}
            disabledByPermission={!hasRolePermission}
          >
            {getAddResidentButtonText(preferredPeopleNominative)}
          </Button>
        </div>
      </div>
    )
  }

  const renderTitle = () => {
    const link = `/residents/list`

    return (
      <Link
        onClick={() => triggerGAEvent({ action: 'Click resident title link' })}
        to={link}
        style={styles.title}
        data-cy="btn-residents"
      >
        <CardTitle title={getTitle(preferredPeopleNominative) || '_'} />
      </Link>
    )
  }

  return (
    <div>
      <div className="lmc-home-card">
        {renderTitle()}
        <div className="lmc-home-card-body-ss" style={styles.offsetBody}>
          {renderBody()}
        </div>
        <div className="lmc-home-card-footer">{renderFooter()}</div>
      </div>
    </div>
  )
}

const styles = {
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  title: {
    display: 'inline-block',
    textDecoration: 'none',
  },
  viewAllButton: {
    marginRight: 8,
    maxWidth: 95,
  },
  offsetBody: {
    transform: 'translateX(-5px)',
  },
}

export default ResidentsCard
